import * as React from "react";
import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {useLocation, useMatch, useParams} from "react-router-dom";

import {useAdminApi} from "../../../api/api-context";
import {ICategoryGroupItem, ICategoryItem, loadCategories} from "../../../utils/category-util";

export interface CategoryCacheContextValue {
    categories?: ICategoryItem[];
    categoryGroups?: ICategoryGroupItem[];
}

const CategoryCacheContext = createContext<CategoryCacheContextValue | undefined>(undefined);

type CategoryCacheProviderProps = React.PropsWithChildren<{}>;

export const CategoryCacheProvider: React.FC<CategoryCacheProviderProps> = ({children}) => {
    const [categories, setCategories] = useState<ICategoryItem[]>();
    const [categoryGroups, setCategoryGroups] = useState<ICategoryGroupItem[]>();
    const adminApi = useAdminApi();
    const {mediaId} = useParams<{mediaId: string}>();
    const location = useLocation();
    // カテゴリを使用するページ（ドキュメント一覧・編集、リビジョン編集）のみロードする
    // また、typeが指定されていないか、ARTICLEの場合に限る
    const type = useMemo(() => new URLSearchParams(location.search).get("type"), [location.search]);
    const matchDocuments = useMatch("/admin/:mediaId/documents/*");
    const matchRevisions = useMatch("/admin/:mediaId/revisions/:documentRevisionId");
    // ドキュメント一覧・編集、リビジョン編集のいずれかのページにいるか
    const isOnCategoryPage = matchDocuments != null || matchRevisions != null;
    const shouldLoad = useMemo(
        () => isOnCategoryPage && (!type || type === "ARTICLE") && !!mediaId,
        [isOnCategoryPage, type, mediaId],
    );

    useEffect(() => {
        if (!shouldLoad) return;
        loadCategories(adminApi, mediaId!)
            .then(({categories, categoryGroups}) => {
                setCategories(categories);
                setCategoryGroups(categoryGroups);
            })
            .catch(console.error);
    }, [adminApi, mediaId, shouldLoad]);

    const value = useMemo(() => ({categories, categoryGroups}), [categories, categoryGroups]);

    return <CategoryCacheContext.Provider value={value}>{children}</CategoryCacheContext.Provider>;
};

export function useCategoryCache() {
    const value = useContext(CategoryCacheContext);
    if (!value) {
        throw new Error("CategoryCacheContextProvider is not provided");
    }
    return value;
}
